// Adapts collection metadata into eventing Recommender payloads.
import type { ContextClass, Recommender } from '@coursera/event-pulse-types';

import type { RecommendationsExplainerInput } from 'bundles/recommendations-common/types';

export type RecommenderSource = {
  __typename?: string;
  id?: string | null;
  recommenderConfigurationHash?: unknown;
  context?: unknown;
  additionalInput?: unknown;
  programs?: unknown;
};

export type CollectionMetadata = {
  recommender?: Recommender;
  recommendationsExplainer?: RecommendationsExplainerInput;
};

function isProgram(program: unknown): program is { id: string } {
  return typeof program === 'object' && program !== null && typeof (program as { id?: unknown }).id === 'string';
}

export function toRecommendationsExplainerPrograms(
  programs: unknown
): ReadonlyArray<{ id: string }> | null | undefined {
  if (programs === null) {
    return null;
  }

  if (!Array.isArray(programs)) {
    return undefined;
  }

  return programs.filter(isProgram).map(({ id }) => ({ id }));
}

export function toRecommender(source: RecommenderSource | null | undefined): Recommender | undefined {
  if (source == null) {
    return undefined;
  }

  const sourceHash = source.recommenderConfigurationHash;
  const hash = typeof sourceHash === 'string' && sourceHash.length > 0 ? sourceHash : '';
  const context = source.context;
  return {
    hash,
    ...(context !== null && context !== undefined ? { context: context as ContextClass } : {}),
  };
}

export function getCollectionMetadata(source: RecommenderSource): CollectionMetadata {
  const recommender = toRecommender(source);
  const programs = toRecommendationsExplainerPrograms(source.programs);
  return {
    ...(recommender ? { recommender } : {}),
    ...(source.__typename === 'Recommendation_Collection' && typeof source.id === 'string' && source.id.length > 0
      ? {
          recommendationsExplainer: {
            id: source.id,
            additionalInput: source.additionalInput as RecommendationsExplainerInput['additionalInput'],
            ...(programs !== undefined ? { programs } : {}),
          },
        }
      : {}),
  };
}
